/**
 * Route Component Registry
 *
 * Single source of truth for all route-to-component mappings.
 * Used by the RBAC wizard to populate the componentPath dropdown
 * and prevent the same component from being assigned to multiple routes.
 *
 * Each entry:
 *   label        – Human-readable name shown in dropdown
 *   value        – componentPath used by dynamicRouter (relative to src/)
 *   category     – Grouping for the dropdown (pages, modules, dashboards, etc.)
 *   defaultRoute – The route this component is typically used for (informational)
 */

const ROUTE_COMPONENT_REGISTRY = [
  // ===== DASHBOARDS =====
  { label: 'AdminDashboard', value: 'pages/AdminDashboard', category: 'Dashboards', defaultRoute: '/admin-dashboard' },
  { label: 'PerformanceDashboard', value: 'pages/PerformanceDashboard', category: 'Dashboards', defaultRoute: '/performance-dashboard' },
  { label: 'SalesDashboard', value: 'pages/SalesDashboard', category: 'Dashboards', defaultRoute: '/sales-dashboard' },
  { label: 'FinanceDashboard', value: 'pages/FinanceDashboard', category: 'Dashboards', defaultRoute: '/financial-dashboard' },
  { label: 'CheckoutSuccessRatePage', value: 'pages/CheckoutSuccessRatePage', category: 'Dashboards', defaultRoute: '/checkout-success-rate' },
  { label: 'CallDashboard', value: 'pages/CallDashboard', category: 'Dashboards', defaultRoute: '/call-dashboard' },
  { label: 'PostSalesDashboard', value: 'pages/PostSalesDashboard', category: 'Dashboards', defaultRoute: '/post-sales-dashboard' },
  { label: 'DoctorsDashboard', value: 'pages/DoctorsDashboard', category: 'Dashboards', defaultRoute: '/home' },
  { label: 'WhatsAppAnalyticsDashboard', value: 'pages/WhatsAppAnalyticsDashboard', category: 'Dashboards', defaultRoute: '/whatsapp-analytics' },
  { label: 'NpsAnalysisDashboardPage', value: 'pages/NpsAnalysisDashboardPage', category: 'Dashboards', defaultRoute: '/nps-dashboard/nps-analysis' },
  { label: 'NpsV2Page', value: 'pages/NpsV2Page', category: 'Dashboards', defaultRoute: '/nps-dashboard/nps-v2' },
  { label: 'AllopathyDoctorDashboard', value: 'pages/AllopathyDoctorDashboard', category: 'Dashboards', defaultRoute: '/clinic-home' },
  { label: 'ProductivityDashboard', value: 'modules/productivity_dashboard/ProductivityDashboard', category: 'Dashboards', defaultRoute: '/productivity-dashboard' },
  { label: 'CallingKpi', value: 'modules/kpi_dashboard/CallingKpi', category: 'Dashboards', defaultRoute: '/kpi-dashboard' },
  { label: 'KpiDashboardHistory', value: 'modules/kpi_dashboard/KpiDashboardHistory', category: 'Dashboards', defaultRoute: '/kpi-historical-dashboard' },
  { label: 'TodayEngagement (Lead)', value: 'modules/medrep_engagement/TodayEngagement', category: 'Dashboards', defaultRoute: '/lead-dashboard' },
  { label: 'ClinicsDashBoard', value: 'modules/clinic-management/ClinicsDashBoard', category: 'Dashboards', defaultRoute: '/clinic-dashboard' },
  { label: 'Dashboard (Main)', value: 'modules/dashboards/Dashboard', category: 'Dashboards', defaultRoute: '/dashboard' },
  { label: 'MedicalKpi', value: 'modules/medical_kpi_dashboard/MedicalKpi', category: 'Dashboards', defaultRoute: '/medical-kpi' },
  { label: 'ComplaintKpi', value: 'modules/complaints_kpi_dashboard/ComplaintKpi', category: 'Dashboards', defaultRoute: '/complaint-kpi' },
  { label: 'TicketingDashboard', value: 'modules/ticketing_dashboard/TicketingDashboard', category: 'Dashboards', defaultRoute: '/ticketing-dashboard' },
  { label: 'ManagerCallDashboard', value: 'modules/manager_call_dashboard/ManagerCallDashboard', category: 'Dashboards', defaultRoute: '/manager-call-attempts' },
  { label: 'CovinDashboard', value: 'modules/covin_dashboard/CovinDashboard', category: 'Dashboards', defaultRoute: '/covin-dashboard' },
  { label: 'FunnelDashboard', value: 'modules/funnel_dashboard', category: 'Dashboards', defaultRoute: '/funnel-dashboard' },
  { label: 'ReportDashboard (Shopify)', value: 'modules/Shopify_dashboard/ReportDashboard', category: 'Dashboards', defaultRoute: '/report-dashboard' },
  { label: 'DashboardDispatch', value: 'pages/dispatchDashboard/DashboardDispatch', category: 'Dashboards', defaultRoute: '/dispatch-dashboard' },

  // ===== TICKETS =====
  { label: 'MyTickets', value: 'pages/MyTickets', category: 'Tickets', defaultRoute: '/my-tickets' },
  { label: 'TicketConfigure', value: 'pages/TicketConfigure', category: 'Tickets', defaultRoute: '/configure-ticket' },
  { label: 'TicketGroups', value: 'pages/TicketGroups', category: 'Tickets', defaultRoute: '/configure/ticket-group-management' },
  { label: 'AllTickets', value: 'pages/AllTickets', category: 'Tickets', defaultRoute: '/all-tickets' },
  { label: 'InternalTickets', value: 'pages/InternalTickets', category: 'Tickets', defaultRoute: '/internal-tickets' },
  { label: 'MedicalEscalationTickets', value: 'modules/my_tickets/MedicalEscalationTickets', category: 'Tickets', defaultRoute: '/medical-esaclation-tickets' },

  // ===== CONFIGURE =====
  { label: 'Medicines', value: 'pages/Medicines', category: 'Configure', defaultRoute: '/configure/medicines' },
  { label: 'Tags', value: 'pages/Tags', category: 'Configure', defaultRoute: '/configure/tags' },
  { label: 'ProductMapping', value: 'pages/ProductMapping', category: 'Configure', defaultRoute: '/configure/product-mapping' },
  { label: 'Settings', value: 'pages/Settings', category: 'Configure', defaultRoute: '/configure/settings' },
  { label: 'OfflineHairTestConfig', value: 'pages/OfflineHairTestConfig', category: 'Configure', defaultRoute: '/configure/offline-hairtest' },
  { label: 'DiscountCodes', value: 'pages/DiscountCodes', category: 'Configure', defaultRoute: '/configure/discount-codes' },
  { label: 'RBACConfigure', value: 'pages/RBACConfigure', category: 'Configure', defaultRoute: '/configure/rbac-management' },
  { label: 'OfflineDoctorRoaster', value: 'pages/OfflineDoctorRoaster', category: 'Configure', defaultRoute: '/configure/offline-doctor-roaster' },
  { label: 'Webinar', value: 'pages/webinar', category: 'Configure', defaultRoute: '/configure/webinar-management' },

  // ===== USER MANAGEMENT =====
  { label: 'UserManagement', value: 'modules/user_management/UserManagement', category: 'User Management', defaultRoute: '/user-management' },
  { label: 'GroupManagement', value: 'pages/GroupManagement', category: 'User Management', defaultRoute: '/group-management' },
  { label: 'SSOT', value: 'modules/user_management/SSOT', category: 'User Management', defaultRoute: '/ssot' },
  { label: 'DeviceMonitoring', value: 'modules/device_monitoring/DeviceMonitoring', category: 'User Management', defaultRoute: '/device-monitoring' },

  // ===== CASE MANAGEMENT =====
  { label: 'AllLeads', value: 'modules/case_management/AllLeads', category: 'Case Management', defaultRoute: '/case-management/all-leads' },
  { label: 'AllLeadsTag', value: 'modules/case_management/AllLeadsTag', category: 'Case Management', defaultRoute: '/case-management/all-cases-tag' },
  { label: 'SalesManagement', value: 'modules/case_management/SalesManagement', category: 'Case Management', defaultRoute: '/case-management/sales-management' },
  { label: 'CaseSlotBooking', value: 'modules/case_management/CaseSlotBooking', category: 'Case Management', defaultRoute: '/case-management/slot-booking' },
  { label: 'VayuLead', value: 'modules/vayu_lead/VayuLead', category: 'Case Management', defaultRoute: '/case-management/vayu-lead' },
  { label: 'AdminCaseManagement', value: 'pages/AdminCaseManagement', category: 'Case Management', defaultRoute: '/admin-case-management' },

  // ===== ORDER MANAGEMENT =====
  { label: 'OrderManagement', value: 'pages/OrderManagement', category: 'Order Management', defaultRoute: '/order-management' },
  { label: 'AllOrdersManagement', value: 'modules/order_management/AllOrdersManagement', category: 'Order Management', defaultRoute: '/order-management/all-orders' },
  { label: 'OrderMovement', value: 'modules/order_management/OrderMovement', category: 'Order Management', defaultRoute: '/order-management' },
  { label: 'UpdateOrders', value: 'pages/UpdateOrders', category: 'Order Management', defaultRoute: '/order-management/update_order' },
  { label: 'OpenOrders', value: 'pages/OpenOrders', category: 'Order Management', defaultRoute: '/order-management/open_orders' },
  { label: 'RecentOrders', value: 'pages/RecentOrders', category: 'Order Management', defaultRoute: '/order-management/recent_orders' },
  { label: 'GhostOrders', value: 'pages/GhostOrders', category: 'Order Management', defaultRoute: '/order-management/ghost-orders' },
  { label: 'CodToPrepaid', value: 'pages/CodToPrepaid', category: 'Order Management', defaultRoute: '/order-management/cod-to-prepaid' },
  { label: 'ReturnOrders', value: 'modules/order_management/ReturnOrders', category: 'Order Management', defaultRoute: '/order-management/return-orders' },
  { label: 'OfflineOrders', value: 'modules/offline_orders/OfflineOrders', category: 'Order Management', defaultRoute: '/order-management/offline-orders' },
  { label: 'RefundManagement', value: 'modules/refund_management/RefundManagement', category: 'Order Management', defaultRoute: '/order-management/refund-management' },

  // ===== REPORTS & ENGAGEMENT =====
  { label: 'Reports', value: 'pages/Reports', category: 'Reports', defaultRoute: '/reports' },
  { label: 'EngagementReports', value: 'pages/EngagementReports', category: 'Reports', defaultRoute: '/engagement-report' },
  { label: 'EngagementRep', value: 'pages/EngagementRep', category: 'Reports', defaultRoute: '/engagement-report' },
  { label: 'MonthReport (MTD)', value: 'modules/mtd_report/MonthReport', category: 'Reports', defaultRoute: '/mtd-report' },

  // ===== FEEDBACK =====
  { label: 'SupportFeedback', value: 'pages/SupportFeedback', category: 'Feedback', defaultRoute: '/feedback' },
  { label: 'CustomerFeedback', value: 'pages/CustomerFeedback', category: 'Feedback', defaultRoute: '/customer-feedback' },
  { label: 'FeedbackTab', value: 'modules/support_feedback/FeedbackTab', category: 'Feedback', defaultRoute: '/feedback' },

  // ===== DOCTOR & MEDICAL =====
  { label: 'DoctorBooking', value: 'pages/DoctorBooking', category: 'Doctor', defaultRoute: '/doctor-booking' },
  { label: 'PrescribedPatients', value: 'pages/PrescribedPatients', category: 'Doctor', defaultRoute: '/prescribed-patients' },
  { label: 'ApprovePrescription', value: 'pages/ApprovePrescription', category: 'Doctor', defaultRoute: '/approve-prescriptions' },
  { label: 'MyAppointment', value: 'pages/MyAppointment', category: 'Doctor', defaultRoute: '/my-appointments' },
  { label: 'AssignedDoctors', value: 'pages/AssignedDoctors', category: 'Doctor', defaultRoute: '/order-management/assigned-doctors' },

  // ===== OPERATIONS =====
  { label: 'Warehouse', value: 'pages/Warehouse', category: 'Operations', defaultRoute: '/warehouse' },
  { label: 'InvoiceGenerate', value: 'pages/InvoiceGenerate', category: 'Operations', defaultRoute: '/invoice' },
  { label: 'WarehousePincodeMapping', value: 'pages/WarehousePincodeMapping', category: 'Operations', defaultRoute: '/warehouse-pincode-mapping' },
  { label: 'RefundTracking', value: 'pages/RefundTracking', category: 'Operations', defaultRoute: '/refund-tracking' },
  { label: 'NaNdrOrders', value: 'pages/NaNdrOrders', category: 'Operations', defaultRoute: '/na-ndr-orders' },
  { label: 'UnshippedOrders', value: 'pages/UnshippedOrders', category: 'Operations', defaultRoute: '/unshipped-orders' },
  { label: 'OTSManagement', value: 'modules/Ots_mangment/OTSManagment', category: 'Operations', defaultRoute: '/order-tracking-service' },
  { label: 'NDRManagement', value: 'modules/Ndr_management/NDRManagement', category: 'Operations', defaultRoute: '/ndr-mapper' },
  { label: 'UploadCsvFutwork', value: 'modules/third_party_upload/UploadCsvFutwork', category: 'Operations', defaultRoute: '/bulk-upload/delhivery' },
  { label: 'Zoho', value: 'modules/zoho/Zoho', category: 'Operations', defaultRoute: '/zoho' },
  { label: 'UniCommerce', value: 'modules/unicommerce/FailedUniOrdersTable', category: 'Operations', defaultRoute: '/uni_commerce_dashboard' },
  { label: 'PincodeManagement', value: 'modules/pincode_management/PincodeManagement', category: 'Operations', defaultRoute: '/pincode-management' },

  // ===== CLINIC =====
  { label: 'Clinics', value: 'modules/clinic-management/Clinics', category: 'Clinic', defaultRoute: '/configure/clinic-management' },
  { label: 'ClinicManagement (Page)', value: 'pages/ClinicManagement', category: 'Clinic', defaultRoute: '/configure/clinic-management' },
  { label: 'StoreVisit', value: 'pages/StoreVisit', category: 'Clinic', defaultRoute: '/store-visit' },
  { label: 'StoreCustomerAssignment', value: 'modules/store-customer-assignment/StoreCustomerAssignment', category: 'Clinic', defaultRoute: '/store-customer-assignment' },
  { label: 'SubscriptionAnalytics', value: 'pages/SubscriptionAnalytics', category: 'Offline', defaultRoute: '/offline/subscription-analytics' },
  { label: 'DoctorConsultationRefund', value: 'pages/DoctorConsultationRefund', category: 'Offline', defaultRoute: '/offline/doctor-consultation-refunds' },

  // ===== MISC PAGES =====
  { label: 'Home', value: 'pages/Home', category: 'Pages', defaultRoute: '/dashboard/home' },
  { label: 'IvrCall', value: 'pages/IvrCall', category: 'Pages', defaultRoute: '/ivr-calls' },
  { label: 'Community', value: 'pages/Community', category: 'Pages', defaultRoute: '/community' },
  { label: 'ManageWeekly', value: 'pages/ManageWeekly', category: 'Pages', defaultRoute: '/manage-weekly-schedules' },
  { label: 'UtmCampaign', value: 'pages/UtmCampaign', category: 'Pages', defaultRoute: '/utm-campaign' },
  { label: 'RevenueTracker', value: 'pages/RevenueTracker', category: 'Pages', defaultRoute: '/revenue-tracking' },
  { label: 'ManageSegment', value: 'pages/ManageSegment', category: 'Pages', defaultRoute: '/manage-segments' },
  { label: 'ManageContents', value: 'pages/ManageContents', category: 'Pages', defaultRoute: '/manage-contents' },
  { label: 'ManageTag', value: 'pages/ManageTag', category: 'Pages', defaultRoute: '/manage-tags' },
  { label: 'ManageComponents', value: 'pages/ManageComponents', category: 'Pages', defaultRoute: '/manage-components' },
  { label: 'MasterTable', value: 'modules/master_table/MasterTable', category: 'Pages', defaultRoute: '/manage-master-table' },
  { label: 'Allocation', value: 'pages/Allocation', category: 'Pages', defaultRoute: '/allocation' },
  { label: 'BulkDeactivate', value: 'pages/BulkDeactivate', category: 'Pages', defaultRoute: '/bulk-deactivate' },
  { label: 'CustomerDataPage', value: 'pages/CustomerDataPage', category: 'Pages', defaultRoute: '/customer-data' },
  { label: 'Escalation', value: 'pages/Escalation', category: 'Pages', defaultRoute: '/escalation' },
  { label: 'SlotBookingLoggers', value: 'pages/SlotBookingLoggers', category: 'Pages', defaultRoute: '/slot-booking-loggers' },
  { label: 'PaymentGateway', value: 'modules/payment_gateway/PayementGateway', category: 'Pages', defaultRoute: '/transactions-dashboard' },
  { label: 'WhatsAppAnalyticsLayout', value: 'modules/whatsapp_analytics/WhatsAppAnalyticsLayout', category: 'Pages', defaultRoute: '/whatsapp-analytics' },
  { label: 'IdeaDashboard', value: 'modules/ideal_time_calculator/IdeaDashboard', category: 'Pages', defaultRoute: '/idealtimecalculator' },

  // ===== QA & INCENTIVES =====
  { label: 'QaAuditDashboard', value: 'modules/qa_audit/QaAuditDashboard', category: 'QA & Incentives', defaultRoute: '/qa-audit' },
  { label: 'QA_questions', value: 'modules/qa_questions/QA_questions', category: 'QA & Incentives', defaultRoute: '/qa-questions' },
  { label: 'CallingIncentives', value: 'modules/allIncentiveDashboard/callingIncentiveDashboard/CallingIncentives', category: 'QA & Incentives', defaultRoute: '/incentives-calculator' },
  { label: 'IncentiveSlab', value: 'modules/allIncentiveDashboard/incentiveSlabDashboard/IncentiveSlab', category: 'QA & Incentives', defaultRoute: '/incentives-slab' },
  { label: 'AchievedScore', value: 'modules/allIncentiveDashboard/incentiveAcheivedScoreDashboard/AchievedScore', category: 'QA & Incentives', defaultRoute: '/incentives-achievedScore' },
  { label: 'WeightageAndTarget', value: 'modules/allIncentiveDashboard/weightageAndTargetDashboard/WeightageAndTarget', category: 'QA & Incentives', defaultRoute: '/weightage-target' },
  { label: 'ComplaintIncentives', value: 'modules/complaints_incentives/ComplaintIncentives', category: 'QA & Incentives', defaultRoute: '/complaints-incentives' },
  { label: 'MedicalIncentives', value: 'modules/medical_incentives/MedicalIncentives', category: 'QA & Incentives', defaultRoute: '/medical-incentives' },

  // ===== ENGAGEMENT DASHBOARDS =====
  { label: 'OutboundSummaryPage', value: 'pages/OutboundSummaryPage', category: 'Engagement Dashboards', defaultRoute: '/engagement-dashboards/outbound-summary' },
  { label: 'EngagementDetailsPage', value: 'pages/EngagementDetailsPage', category: 'Engagement Dashboards', defaultRoute: '/engagement-dashboards/engagement-details' },
  { label: 'SlotPendencyPage', value: 'pages/SlotPendencyPage', category: 'Engagement Dashboards', defaultRoute: '/engagement-dashboards/slot-pendency' },
  { label: 'DailyPendencyPage', value: 'pages/DailyPendencyPage', category: 'Engagement Dashboards', defaultRoute: '/engagement-dashboards/daily-pendency' },
  { label: 'AgentLoginReportPage', value: 'pages/AgentLoginReportPage', category: 'Engagement Dashboards', defaultRoute: '/engagement-dashboards/agent-login-report' },
  { label: 'AgentPerformancePage', value: 'pages/AgentPerformancePage', category: 'Engagement Dashboards', defaultRoute: '/engagement-dashboards/agent-performance' },
];

/**
 * Get available (unassigned) components for a dropdown.
 * Filters out components already assigned to other routes in the permissions object.
 *
 * @param {object} permissions   – The current localPermissions object
 * @param {string} [currentPath] – The route path being edited (its own componentPath is NOT excluded)
 * @returns {Array<{label: string, value: string, category: string}>}
 */
export const getAvailableComponents = (permissions, currentPath) => {
  // Collect all componentPaths already in use
  const usedPaths = new Set();

  Object.entries(permissions || {}).forEach(([groupName, groupData]) => {
    if (groupName === '__settings' || !groupData?.routes) return;

    Object.entries(groupData.routes).forEach(([routePath, routeData]) => {
      if (routeData.componentPath && routePath !== currentPath) {
        usedPaths.add(routeData.componentPath);
      }
      // Check subroutes too
      Object.entries(routeData.subRoutes || {}).forEach(([, subData]) => {
        if (subData.componentPath && subData.path !== currentPath) {
          usedPaths.add(subData.componentPath);
        }
      });
    });
  });

  return ROUTE_COMPONENT_REGISTRY.map(entry => ({
    ...entry,
    label: usedPaths.has(entry.value)
      ? `${entry.label}  (assigned)`
      : entry.label,
    disabled: usedPaths.has(entry.value),
  }));
};

/**
 * Group components by category for <Select optionGroup> rendering.
 *
 * @param {Array} components – Output of getAvailableComponents()
 * @returns {Array<{label: string, options: Array}>} – Grouped options for Ant Design Select
 */
export const groupComponentsByCategory = (components) => {
  const groups = {};
  components.forEach(comp => {
    const cat = comp.category || 'Other';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(comp);
  });

  return Object.entries(groups).map(([category, options]) => ({
    label: category,
    options,
  }));
};

/**
 * Resolve the default component for a route path by matching against the registry's defaultRoute field.
 * Returns the full registry entry or null if no match found.
 *
 * @param {string} routePath – e.g. "/dashboard", "/myTickets"
 * @returns {{ label: string, value: string, category: string, defaultRoute: string } | null}
 */
export const getDefaultComponentForRoute = (routePath) => {
  if (!routePath) return null;
  return ROUTE_COMPONENT_REGISTRY.find(entry => entry.defaultRoute === routePath) || null;
};

/**
 * Default landing route per role. Used by homeRoute() in utility.js as fallback
 * when no RBAC-configured default route exists.
 */
const ROLE_DEFAULT_ROUTES = {
  community: '/my-tickets?tab=assigned',
  consult_doctor: '/dashboard',
  support: '/my-tickets?tab=assigned',
  quality: '/feedback?tab=Build-a-Plan&pag=1',
  chat: '/my-tickets?tab=assigned',
  orm: '/my-tickets?tab=assigned',
  medical: '/dashboard?tab=home',
  lead_agent: '/lead-dashboard',
  chat_call_agent: '/lead-dashboard',
  operations_admin: '/my-tickets?tab=assigned',
  doctor: '/dashboard?tab=home',
  operations: '/dashboard?tab=home',
  admin: '/admin-dashboard?tab=salesDashboard',
  super_admin: '/report-dashboard',
  team_lead: '/my-tickets?tab=assigned',
  medrep_ext_ss: '/my-tickets?tab=assigned',
  medrep_ext_cnx: '/my-tickets?tab=assigned',
  sales: '/dashboard?tab=actionable',
  clinic_receptionist: '/clinic-dashboard',
  clinic_doctor: '/my-appointments',
};

/**
 * Get the default route for a role.
 * @param {string} role - Role name (e.g. "admin", "doctor")
 * @returns {string|undefined}
 */
export const getDefaultRouteForRole = (role) => ROLE_DEFAULT_ROUTES[role];

export default ROUTE_COMPONENT_REGISTRY;
